import {
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  Loader2,
  Search,
  ShieldAlert,
  TriangleAlert,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  getLatestAccountAudit,
  runAccountAudit,
  type AccountAuditFinding,
  type AccountAuditResponse,
  type AccountAuditStatus,
} from '@/services/googleAdsAccountAuditService';
import { detectAccessibleGoogleAdsCustomers } from '@/services/googleAdsOAuthService';
import { apiService } from '@/services/api';

interface AccountAuditTabProps {
  tenantId: string;
}

export function AccountAuditTab({ tenantId }: AccountAuditTabProps) {
  const queryClient = useQueryClient();
  const latestQueryKey = ['google-ads', 'account-audit', 'latest', tenantId];
  const latestAuditQuery = useQuery({
    queryKey: latestQueryKey,
    queryFn: () => getLatestAccountAudit(tenantId),
    enabled: Boolean(tenantId),
    retry: false,
  });

  const auditMutation = useMutation({
    mutationFn: () => runAccountAudit(tenantId),
    onSuccess: (result) => {
      queryClient.setQueryData(latestQueryKey, result);
      if (!result.run_id) {
        toast.warning('Audit complete, but the saved-run cache was not written.');
      } else if (result.summary.failed > 0) {
        toast.warning(
          `Audit complete: ${result.summary.failed} failed check${result.summary.failed !== 1 ? 's' : ''}`,
        );
      } else if (result.summary.warnings > 0) {
        toast.info(
          `Audit complete: ${result.summary.warnings} warning${result.summary.warnings !== 1 ? 's' : ''}`,
        );
      } else {
        toast.success('Audit complete: no blocking setup issues.');
      }
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Audit failed: ${detail}`);
    },
  });

  const applyCustomerIdMutation = useMutation({
    mutationFn: async () => {
      const detected = await detectAccessibleGoogleAdsCustomers(tenantId);
      const customerId = detected.recommended_customer_id;
      if (!customerId) {
        if (detected.customers.length > 1) {
          throw new Error(
            'Google Ads returned multiple customer IDs. Open Workspace Settings to choose the right client account.'
          );
        }
        throw new Error('Google Ads OAuth did not return any accessible customer IDs.');
      }
      const saved = await apiService.updateTenant(tenantId, {
        google_ad_customer_id: customerId,
      });
      return saved.google_ad_customer_id || customerId;
    },
    onSuccess: async (customerId) => {
      toast.success(`Google Ads customer ID ${formatCustomerId(customerId)} applied.`);
      await queryClient.invalidateQueries({ queryKey: ['tenantSettings', tenantId] });
      auditMutation.mutate();
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? (err instanceof Error ? err.message : String(err));
      toast.error(`Customer ID detection failed: ${detail}`);
    },
  });

  const result = auditMutation.data ?? latestAuditQuery.data;

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              Tenant
            </label>
            <div className="rounded border border-border bg-warm-bg px-3 py-2 text-sm">
              {tenantId}
            </div>
          </div>
          <div className="min-w-[220px] space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              Latest saved run
            </label>
            <div className="rounded border border-border bg-warm-bg px-3 py-2 text-sm">
              {latestAuditQuery.isLoading
                ? 'Loading...'
                : result
                  ? new Date(result.generated_at).toLocaleString()
                  : 'No saved audit yet'}
            </div>
          </div>
          <Button
            onClick={() => auditMutation.mutate()}
            disabled={auditMutation.isPending}
            className="bg-coral hover:bg-coral-hover"
          >
            {auditMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Running audit
              </>
            ) : (
              <>
                <ClipboardCheck className="mr-2 h-4 w-4" />
                Run audit
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {result && (
        <AuditResult
          result={result}
          detectingCustomerId={applyCustomerIdMutation.isPending}
          onDetectCustomerId={() => applyCustomerIdMutation.mutate()}
        />
      )}
    </div>
  );
}

function AuditResult({
  result,
  detectingCustomerId,
  onDetectCustomerId,
}: {
  result: AccountAuditResponse;
  detectingCustomerId: boolean;
  onDetectCustomerId: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <SummaryCard label="Passed" value={result.summary.passed} tone="pass" />
        <SummaryCard label="Warnings" value={result.summary.warnings} tone="warning" />
        <SummaryCard label="Failed" value={result.summary.failed} tone="fail" />
        <SummaryCard label="Blocking" value={result.summary.blocking} tone="fail" />
      </div>

      {/*
        WHY THIS LINE EXISTS. The audit only counts inventory that can actually
        serve, which removes 85-91% of the rows it used to read. Without saying
        so, a much smaller finding count looks like the tool broke rather than
        like it stopped reporting on paused inventory nobody can act on.
        Absent on runs cached before this shipped, so it renders conditionally.
      */}
      {result.summary.scope_note ? (
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">What was audited: </span>
          {result.summary.scope_note}
        </p>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {result.summary.blocking > 0 ? (
              <ShieldAlert className="h-4 w-4 text-coral" />
            ) : (
              <CheckCircle2 className="h-4 w-4 text-teal" />
            )}
            Findings
            <Badge variant="outline" className="ml-2">
              {result.customer_id ? `CID ${result.customer_id}` : 'No customer ID'}
            </Badge>
          </CardTitle>
          <CardDescription>
            Generated {new Date(result.generated_at).toLocaleString()}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Check</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Evidence</TableHead>
                <TableHead>Recommendation</TableHead>
                <TableHead>Scope</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.findings.map((finding) => (
                <FindingRow
                  key={finding.id}
                  finding={finding}
                  detectingCustomerId={detectingCustomerId}
                  onDetectCustomerId={onDetectCustomerId}
                />
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: AccountAuditStatus;
}) {
  const toneClass =
    tone === 'pass'
      ? 'border-teal/30 bg-teal-light/30 text-teal-dark'
      : tone === 'warning'
        ? 'border-coral/30 bg-coral-light/30 text-coral-dark'
        : 'border-destructive/20 bg-destructive/5 text-destructive';

  return (
    <Card className={toneClass}>
      <CardContent className="p-4">
        <div className="text-xs font-medium uppercase tracking-wide opacity-70">
          {label}
        </div>
        <div className="text-2xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}

function FindingRow({
  finding,
  detectingCustomerId,
  onDetectCustomerId,
}: {
  finding: AccountAuditFinding;
  detectingCustomerId: boolean;
  onDetectCustomerId: () => void;
}) {
  const canDetectCustomerId = finding.id === 'customer_id' && finding.status === 'fail';

  return (
    <TableRow>
      <TableCell>
        <div className="font-medium">{finding.title}</div>
        <div className="text-xs text-muted-foreground">{finding.category}</div>
      </TableCell>
      <TableCell>
        <StatusBadge status={finding.status} />
        {finding.severity === 'blocking' && (
          <div className="mt-1 flex items-center gap-1 text-xs text-destructive">
            <TriangleAlert className="h-3 w-3" />
            Blocking
          </div>
        )}
      </TableCell>
      <TableCell className="max-w-[280px] text-sm text-muted-foreground">
        {finding.evidence}
      </TableCell>
      <TableCell className="max-w-[320px] text-sm">
        <div className="space-y-2">
          <div>{finding.recommendation}</div>
          {finding.action_url && finding.action_label && (
            <Button
              asChild
              size="sm"
              variant="outline"
              className="h-7 border-teal/40 text-teal-dark hover:bg-teal-light"
            >
              <Link to={finding.action_url}>
                {finding.action_label}
                <ArrowRight className="ml-1.5 h-3 w-3" />
              </Link>
            </Button>
          )}
          {canDetectCustomerId && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 border-coral/40 text-coral-dark hover:bg-coral-light"
              disabled={detectingCustomerId}
              onClick={onDetectCustomerId}
            >
              {detectingCustomerId ? (
                <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
              ) : (
                <Search className="mr-1.5 h-3 w-3" />
              )}
              Detect and apply
            </Button>
          )}
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="capitalize">
          {finding.action_scope.replaceAll('_', ' ')}
        </Badge>
      </TableCell>
    </TableRow>
  );
}

function formatCustomerId(value: string | null | undefined) {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return digits || 'unknown';
}

function StatusBadge({ status }: { status: AccountAuditStatus }) {
  if (status === 'pass') {
    return (
      <Badge variant="outline" className="border-teal/40 bg-teal-light text-teal-dark">
        Pass
      </Badge>
    );
  }
  if (status === 'warning') {
    return (
      <Badge variant="outline" className="border-coral/40 bg-coral-light text-coral-dark">
        Warning
      </Badge>
    );
  }
  return <Badge variant="destructive">Fail</Badge>;
}
